import React, { useEffect, useState, useRef } from 'react'
import { useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js'
import { api, publicApi, hasSession } from '../../../lib/api'
import { Loader2, AlertCircle } from 'lucide-react'
import FeeDisclosure from '../../../components/giving/FeeDisclosure'
import FundUseDisclosure from '../../../components/giving/FundUseDisclosure'

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''

// Make sure to call `loadStripe` outside of a component's render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null

// Global state to prevent duplicate session creation and multiple checkout instances
let sessionCreationPromise = null
let currentClientSecret = null
let currentSessionKey = null

export default function DonorCheckout() {
  const [searchParams] = useSearchParams()
  const params = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [clientSecret, setClientSecret] = useState(null)
  const [signedIn, setSignedIn] = useState(null)
  const [parish, setParish] = useState(null)
  const mountedRef = useRef(false)

  // `/donate/:parishId` (new, public) falls back to the legacy
  // `/app/donor/checkout?parishId=` query param.
  const parishId = params.parishId || searchParams.get('parishId')
  const amount = searchParams.get('amount') // Amount in dollars, will convert to cents

  const backHref = signedIn === false ? `/parishes/${parishId}` : '/app/donor/donations'
  const backLabel = signedIn === false ? 'Back to Parish' : 'Back to Donations'

  // Parish details feed the fee / fund-use disclosures. A failed lookup must
  // never block checkout, so it just leaves the disclosures out.
  useEffect(() => {
    if (!parishId) return undefined
    let cancelled = false
    Promise.resolve()
      .then(() => publicApi.getParish(parishId))
      .then((p) => {
        if (!cancelled && p) setParish(p)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [parishId])

  useEffect(() => {
    // Prevent multiple initializations (React StrictMode double-mounting)
    if (mountedRef.current) {
      return
    }

    mountedRef.current = true

    // Create checkout session
    const createSession = async () => {
      try {
        setLoading(true)
        setError(null)

        // Resolve session state first so error-state navigation (below) always
        // knows whether "/app/donor/donations" is reachable for this visitor.
        const isSignedIn = await hasSession()
        setSignedIn(isSignedIn)

        if (!parishId || !amount) {
          throw new Error('Missing required parameters: parishId and amount are required')
        }

        if (!STRIPE_PUBLISHABLE_KEY) {
          throw new Error('Stripe is not configured. Please contact support.')
        }

        // Convert amount from dollars to cents
        const amountInCents = Math.round(parseFloat(amount) * 100)
        if (isNaN(amountInCents) || amountInCents < 50) {
          throw new Error('Invalid amount. Minimum donation is $0.50')
        }

        // Create a unique key for this session. Sign-in state is part of the key:
        // this page is dual-mode (guest vs. authed checkout), so a visitor who
        // signs in (or out) without leaving the page must get a fresh session
        // rather than reusing one created under the other identity.
        const sessionKey = `${parishId}-${amountInCents}-${isSignedIn}`

        // If we already have a client secret for this exact session, reuse it
        if (currentClientSecret && currentSessionKey === sessionKey) {
          console.log('Reusing existing checkout session')
          setClientSecret(currentClientSecret)
          setLoading(false)
          return
        }

        // Session key changed — clear stale state from previous donation
        currentClientSecret = null
        currentSessionKey = null

        // If there's already a session creation in progress for this key, wait for it
        if (sessionCreationPromise) {
          sessionCreationPromise = null
        }

        // Build return URL with session_id parameter
        const returnUrl = `${window.location.origin}/donate/return?session_id={CHECKOUT_SESSION_ID}`

        // Create a promise for session creation to prevent duplicates
        sessionCreationPromise = (async () => {
          const response = isSignedIn
            ? await api.createCheckoutSession({ parishId, amount: amountInCents, returnUrl })
            : await publicApi.createGuestCheckoutSession({ parishId, amount: amountInCents, returnUrl })
          const secret = response.clientSecret
          currentClientSecret = secret
          currentSessionKey = sessionKey
          return secret
        })()

        const secret = await sessionCreationPromise
        setClientSecret(secret)
      } catch (err) {
        console.error('Failed to create checkout session:', err)
        setError(err.message || 'Failed to initialize checkout. Please try again.')
        sessionCreationPromise = null
      } finally {
        setLoading(false)
      }
    }

    createSession()

    // No cleanup: mountedRef must stay true for the lifetime of this
    // component instance. React 18 StrictMode double-invokes this effect
    // (mount -> cleanup -> mount) on the SAME instance/refs in dev; resetting
    // mountedRef here would defeat the guard above and create two Stripe
    // Embedded Checkout sessions, which Stripe.js rejects with
    // "You cannot have multiple Embedded Checkout objects."
  }, [parishId, amount])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto mb-4" />
          <p className="text-charcoal-muted">Loading checkout...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded p-6 mb-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-red-600 font-semibold mb-2">Error</h3>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate(backHref)}
          className="px-4 py-2 bg-gold text-black rounded hover:bg-gold/90 transition-colors"
        >
          {backLabel}
        </button>
      </div>
    )
  }

  if (!clientSecret) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded p-6 mb-4">
          <p className="text-red-600 text-sm">No checkout session available. Please try again.</p>
        </div>
        <button
          onClick={() => navigate(backHref)}
          className="px-4 py-2 bg-gold text-black rounded hover:bg-gold/90 transition-colors"
        >
          {backLabel}
        </button>
      </div>
    )
  }

  const options = { clientSecret }

  // Only render checkout if we have a client secret and component is mounted
  if (!clientSecret || !mountedRef.current) {
    return null
  }

  return (
    <div className="w-full h-screen flex flex-col">
      {parish && (
        <div className="px-4 pb-3">
          <FundUseDisclosure parishName={parish.name} fundUse={parish.giving?.fundUse} />
          <FeeDisclosure
            percent={parish.giving?.fee?.oneTimePercent}
            fixedCents={parish.giving?.fee?.fixedCents}
            parishName={parish.name}
          />
        </div>
      )}
      <div className="w-full border-2 border-gold">
        <EmbeddedCheckoutProvider
          key={clientSecret} // Use clientSecret as key to ensure only one instance
          stripe={stripePromise}
          options={options}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    </div>
  )
}
